/* ===================== AUTHOR MARKUP (PREVIEW ONLY) =====================
   The astrologer writes plain text, not HTML: the API escapes everything it
   is sent and rebuilds the small tag set below itself, so a pasted <script>
   or an onerror= never reaches the public blog (the article page injects
   `content` with innerHTML).

   This module renders the SAME subset in the browser, for the composer's
   live preview. It is a convenience, not the contract — the server
   (apps/api/src/utils/blogMarkup.ts) is the authority, and what it returns
   in `content` after a save is the real article. If the two ever disagree,
   the server is right and this file is the bug.

   The supported syntax is listed for the author from BLOG_MARKUP_HELP
   (@astro/api), so the cheatsheet and the parser cannot drift apart.
   ======================================================================== */

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const safeUrl = (url) => (/^https?:\/\/[^\s"'<>]+$/i.test(url.trim()) ? url.trim() : null)

/** Inline marks, applied to text that is ALREADY escaped. */
function inline(escaped) {
  return escaped
    .replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (whole, label, rawUrl) => {
      const href = safeUrl(rawUrl.replace(/&amp;/g, '&'))
      return href ? `<a href="${escapeHtml(href)}" rel="nofollow noopener" target="_blank">${label}</a>` : whole
    })
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
}

const joinLines = (lines) => inline(lines.map(escapeHtml).join('\n')).replace(/\n/g, '<br />')
const listItems = (items) => items.map((item) => `<li>${inline(escapeHtml(item))}</li>`).join('')

/** Author text → the HTML the article will be published as. */
export function renderAuthorContent(source) {
  const lines = String(source ?? '').replace(/\r\n?/g, '\n').split('\n')
  const blocks = []
  let open = null
  const close = () => {
    if (open) blocks.push(open)
    open = null
  }

  for (const raw of lines) {
    const line = raw.trim()
    if (!line) {
      close()
      continue
    }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
      close()
      blocks.push({ kind: 'hr' })
      continue
    }
    const heading = /^(#{2,3})\s+(.*)$/.exec(line)
    if (heading) {
      close()
      blocks.push({ kind: heading[1].length === 2 ? 'h2' : 'h3', lines: [heading[2]] })
      continue
    }
    const bullet = /^[-*]\s+(.*)$/.exec(line)
    if (bullet) {
      if (open?.kind !== 'ul') {
        close()
        open = { kind: 'ul', items: [] }
      }
      open.items.push(bullet[1])
      continue
    }
    const numbered = /^\d+[.)]\s+(.*)$/.exec(line)
    if (numbered) {
      if (open?.kind !== 'ol') {
        close()
        open = { kind: 'ol', items: [] }
      }
      open.items.push(numbered[1])
      continue
    }
    const quote = /^>\s?(.*)$/.exec(line)
    if (quote) {
      if (open?.kind !== 'quote') {
        close()
        open = { kind: 'quote', lines: [] }
      }
      open.lines.push(quote[1])
      continue
    }
    if (open?.kind !== 'p') {
      close()
      open = { kind: 'p', lines: [] }
    }
    open.lines.push(line)
  }
  close()

  return blocks
    .map((block) => {
      switch (block.kind) {
        case 'hr':
          return '<hr />'
        case 'h2':
          return `<h2>${joinLines(block.lines)}</h2>`
        case 'h3':
          return `<h3>${joinLines(block.lines)}</h3>`
        case 'quote':
          return `<blockquote><p>${joinLines(block.lines)}</p></blockquote>`
        case 'ul':
          return `<ul>${listItems(block.items)}</ul>`
        case 'ol':
          return `<ol>${listItems(block.items)}</ol>`
        default:
          return `<p>${joinLines(block.lines)}</p>`
      }
    })
    .join('\n')
}

/** Rough word count on the author's source, for the "x min read" hint. */
export function readingStats(source) {
  const words = String(source ?? '').trim().split(/\s+/).filter(Boolean).length
  return { words, minutes: Math.max(1, Math.round(words / 200)) }
}
